import React, { lazy } from 'react';
import { QuoteIcon } from 'lucide-react';
export function FounderStory() {
  return <section className="relative w-full py-24 md:py-32 overflow-hidden" data-id="element-1747">
      <div className="relative max-w-[1280px] mx-auto px-6 md:px-8" data-id="element-1748">
        {/* Section header */}
        <div className="text-center mb-12 md:mb-16" data-id="element-1749">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#3ab795]/10 border border-[#3ab795]/20 mb-6" data-id="element-1750">
            <div className="w-2 h-2 rounded-full bg-[#3ab795] animate-pulse" data-id="element-1751" />
            <span className="text-sm md:text-base font-medium text-[#3ab795] uppercase tracking-wide" data-id="element-1752">
              Our Story
            </span>
          </div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-[#1D1D1D] mb-4 tracking-tight leading-tight" data-id="element-1753">
            Built By An Investor,{' '}
            <span className="text-[#3ab795]" data-id="element-1754">For Investors</span>
          </h2>
        </div>

        {/* Two-column layout - smaller left column */}
        <div className="grid lg:grid-cols-[35%_1fr] gap-12 lg:gap-16 items-center" data-id="element-1755">
          {/* Left: Founder image - smaller */}
          <div className="relative" data-id="element-1756">
            <div className="relative rounded-2xl overflow-hidden bg-[#f8f9fa] border border-gray-200 shadow-lg" data-id="element-1757">
              {/* Founder image */}
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/3EZ5pXAfTUzy4drXbrurhB/Black_and_White_Modern_Man_YouTube_Profile_Picture_%281%29.png" alt="Cameron Enck - Founder of AcquireFlow" className="w-full h-auto object-cover" style={{
              aspectRatio: '4/5',
              minHeight: '400px'
            }} loading="eager" onError={e => {
              console.error('Image failed to load:', e);
              e.currentTarget.style.display = 'block';
            }} data-id="element-1758" />

              {/* Decorative glow */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#3ab795]/10 via-transparent to-transparent pointer-events-none" data-id="element-1759" />
            </div>

            {/* Floating stat cards */}
            <div className="absolute -bottom-6 -right-6 bg-[#f8f9fa] border border-gray-200 rounded-xl p-4 backdrop-blur-sm shadow-xl z-10" data-id="element-1760">
              <div className="text-3xl font-bold text-[#3ab795] mb-1" data-id="element-1761">400+</div>
              <div className="text-sm text-gray-600" data-id="element-1762">Deals Closed</div>
            </div>

            <div className="absolute -top-6 -left-6 bg-[#f8f9fa] border border-gray-200 rounded-xl p-4 backdrop-blur-sm shadow-xl z-10" data-id="element-1763">
              <div className="text-3xl font-bold text-[#3ab795] mb-1" data-id="element-1764">4+</div>
              <div className="text-sm text-gray-600" data-id="element-1765">Years Investing</div>
            </div>
          </div>

          {/* Right: Story content */}
          <div className="space-y-6" data-id="element-1766">
            {/* Quote icon */}
            <div className="w-12 h-12 rounded-full bg-[#3ab795]/10 flex items-center justify-center" data-id="element-1767">
              <QuoteIcon className="w-6 h-6 text-[#3ab795]" data-id="element-1768" />
            </div>

            {/* Story text */}
            <div className="space-y-4 text-lg text-gray-600 leading-relaxed" data-id="element-1769">
              <p data-id="element-1770">
                <span className="text-[#1D1D1D] font-semibold" data-id="element-1771">
                  I built my business off-market
                </span>{' '}
                — cold calling, PPL, PPC, direct mail. It worked, but as I grew,
                the costs grew with me. I wanted an acquisition channel that was
                scalable, affordable, and didn't require hiring more callers or
                throwing money at marketing.
              </p>

              <p data-id="element-1772">
                The MLS had thousands of motivated sellers already in plain
                sight — the real issue wasn't finding them, it was{' '}
                <span className="text-[#3ab795] font-semibold" data-id="element-1773">
                  getting enough offers out consistently
                </span>
                . Manually submitting offers wasn't realistic or effective. At
                scale, speed matters. Automating your offer flow gives you the
                volume you need to consistently win deals.
              </p>

              <p data-id="element-1774">
                So I built AcquireFlow. What started as a tool for my own
                business quickly became something other investors needed. Today,
                AcquireFlow helps{' '}
                <span className="text-[#1D1D1D] font-semibold" data-id="element-1775">
                  500+ investors send 50,000+ offers every month
                </span>
                —all on complete autopilot.
              </p>

              <p className="text-gray-600 italic border-l-4 border-[#3ab795] pl-4" data-id="element-1776">
                "Scaling gets easier the moment your offer flow runs
                automatically — and that's exactly what AcquireFlow is built
                for."
              </p>
            </div>

            {/* Founder signature */}
            <div className="pt-6 border-t border-gray-200" data-id="element-1777">
              <div className="flex items-center gap-4" data-id="element-1778">
                <div data-id="element-1779">
                  <div className="text-xl font-bold text-[#1D1D1D]" data-id="element-1780">
                    Cameron Enck
                  </div>
                  <div className="text-sm text-gray-600" data-id="element-1781">
                    Founder & CEO, AcquireFlow
                  </div>
                </div>
              </div>
            </div>

            {/* Optional CTA */}
            <div className="pt-4" data-id="element-1782">
              <a href="/trial" className="inline-flex items-center gap-2 px-6 py-3 bg-[#3ab795] text-white font-semibold rounded-xl hover:bg-[#2d9f7e] transition-all duration-300 hover:scale-105 shadow-[0_4px_16px_rgba(58,183,149,0.3)] hover:shadow-[0_8px_24px_rgba(58,183,149,0.4)]" data-id="element-1783">
                Join Beta
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-1784">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" data-id="element-1785" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}